import { apiRequest, downloadFile, postFormData } from "@/lib/api-client-v2";
import type {
  GoEntityCreatePayload,
  GoEntityDetail,
  GoEntityListResponse,
  GoEntityMapGeometryItem,
  GoEntityMeta,
  GoEntityPatchPayload,
  GoGeneralInfoSectionResolve,
  GoGeneralInfoTreeNode,
  GoImportJob,
  GoLinkedCountryItem,
  GoSeedCoverage,
  GoMapGeometryCreatePayload,
  GoMaterialCreatePayload,
  GoMaterialItem,
  GoMaterialKindMetaItem,
} from "@/types/go-entity";
import type {
  GoCountryProfileMetaItem,
  GoEntityCardAuditEntry,
  GoEntityCardResponse,
  GoEntityCardUpdateRequest,
  GoLegitimacyOption,
  GoPrepareClassifierRequest,
  GoPrepareClassifierResponse,
} from "@/types/go-entity-card";
import type { GoReverseGeocodeRequest, GoReverseGeocodeResponse } from "@/types/go-reverse-geocode";
import type {
  GoRegionLinkCreateRequest,
  GoRegionLinkItem,
  GoRegionLinkListResponse,
  GoRegionLinkRefreshResponse,
} from "@/types/module-spatial-bridges";
import type { GoEntityCompareResponse } from "@/types/go-entity-compare";
import type { Geometry } from "@/types/api-v2";

class GoEntitiesService {
  private readonly baseUrl = "/go-entities";

  async list(params?: {
    skip?: number;
    limit?: number;
    q?: string;
    include_deleted?: boolean;
    deleted_only?: boolean;
    has_udgspz?: boolean;
    entity_type_kls_id?: number;
    kls_id_section?: number;
    general_info_tree_kls_id?: number;
    parent_ginf_obj_id?: number;
    roots_only?: boolean;
  }): Promise<GoEntityListResponse> {
    return apiRequest<GoEntityListResponse>(this.baseUrl, {
      params: {
        skip: params?.skip ?? 0,
        limit: params?.limit ?? 50,
        ...(params?.q ? { q: params.q } : {}),
        ...(params?.include_deleted ? { include_deleted: true } : {}),
        ...(params?.deleted_only ? { deleted_only: true } : {}),
        ...(params?.has_udgspz != null ? { has_udgspz: params.has_udgspz } : {}),
        ...(params?.entity_type_kls_id != null
          ? { entity_type_kls_id: params.entity_type_kls_id }
          : {}),
        ...(params?.kls_id_section != null ? { kls_id_section: params.kls_id_section } : {}),
        ...(params?.general_info_tree_kls_id != null
          ? { general_info_tree_kls_id: params.general_info_tree_kls_id }
          : {}),
        ...(params?.parent_ginf_obj_id != null
          ? { parent_ginf_obj_id: params.parent_ginf_obj_id }
          : {}),
        ...(params?.roots_only ? { roots_only: true } : {}),
      },
    });
  }

  async softDelete(ginfObjId: number): Promise<void> {
    await apiRequest<void>(`${this.baseUrl}/${ginfObjId}`, { method: "DELETE" });
  }

  async restore(ginfObjId: number): Promise<GoEntityDetail> {
    return apiRequest<GoEntityDetail>(`${this.baseUrl}/${ginfObjId}/restore`, {
      method: "POST",
    });
  }

  async getById(ginfObjId: number): Promise<GoEntityDetail> {
    return apiRequest<GoEntityDetail>(`${this.baseUrl}/${ginfObjId}`);
  }

  async create(payload: GoEntityCreatePayload): Promise<GoEntityDetail> {
    return apiRequest<GoEntityDetail>(this.baseUrl, {
      method: "POST",
      data: payload,
    });
  }

  async getMeta(): Promise<GoEntityMeta> {
    return apiRequest<GoEntityMeta>(`${this.baseUrl}/meta`);
  }

  async getGeneralInfoTree(): Promise<GoGeneralInfoTreeNode[]> {
    return apiRequest<GoGeneralInfoTreeNode[]>(`${this.baseUrl}/meta/general-info-tree`);
  }

  async resolveGeneralInfoSection(treeKlsId: number): Promise<GoGeneralInfoSectionResolve> {
    return apiRequest<GoGeneralInfoSectionResolve>(
      `${this.baseUrl}/meta/general-info-section-resolve`,
      { params: { tree_kls_id: treeKlsId } },
    );
  }

  async listLinkedCountries(ginfObjId: number): Promise<GoLinkedCountryItem[]> {
    return apiRequest<GoLinkedCountryItem[]>(`${this.baseUrl}/${ginfObjId}/linked-countries`);
  }

  async reverseGeocode(payload: GoReverseGeocodeRequest): Promise<GoReverseGeocodeResponse> {
    return apiRequest<GoReverseGeocodeResponse>(`${this.baseUrl}/reverse-geocode`, {
      method: "POST",
      data: payload,
    });
  }

  async patch(ginfObjId: number, payload: GoEntityPatchPayload): Promise<GoEntityDetail> {
    return apiRequest<GoEntityDetail>(`${this.baseUrl}/${ginfObjId}`, {
      method: "PATCH",
      data: payload,
    });
  }

  async getCard(ginfObjId: number, hubTab?: string): Promise<GoEntityCardResponse> {
    return apiRequest<GoEntityCardResponse>(`${this.baseUrl}/${ginfObjId}/card`, {
      params: hubTab ? { hub_tab: hubTab } : undefined,
    });
  }

  async getSummary(ginfObjId: number): Promise<{
    ginf_obj_id: number;
    obj_name?: string | null;
    iso_alpha3?: string | null;
    entity_type_name?: string | null;
    map_geometry_count?: number;
  }> {
    return apiRequest(`${this.baseUrl}/summary/${ginfObjId}`);
  }

  async dryRunRegionImport(): Promise<{ regions: number; districts: number; geometry_note?: string }> {
    return apiRequest(`${this.baseUrl}/import/regions/dry-run`, { method: "POST" });
  }

  async importRegions(): Promise<{ regions: number; districts: number }> {
    return apiRequest(`${this.baseUrl}/import/regions`, { method: "POST" });
  }

  async seedOk20Template(): Promise<{ template_id: number; status: string }> {
    return apiRequest(`${this.baseUrl}/admin/seed-ok20-template`, { method: "POST" });
  }

  async seedOk20Full(): Promise<{
    template_id: number;
    status: string;
    fields_added?: number;
    fields_parsed?: number;
  }> {
    return apiRequest(`${this.baseUrl}/admin/seed-ok20-full`, { method: "POST" });
  }

  async seedIsoOksm(): Promise<{ upserted: number; isos_found: number }> {
    return apiRequest(`${this.baseUrl}/admin/seed-iso-oksm`, { method: "POST" });
  }

  async listChoroplethMetrics(): Promise<Array<{ code: string; label: string }>> {
    return apiRequest(`${this.baseUrl}/map/choropleth-metrics`);
  }

  async putCard(ginfObjId: number, payload: GoEntityCardUpdateRequest): Promise<GoEntityCardResponse> {
    return apiRequest<GoEntityCardResponse>(`${this.baseUrl}/${ginfObjId}/card`, {
      method: "PUT",
      data: payload,
    });
  }

  async listCardAudit(
    ginfObjId: number,
    params?: { limit?: number },
  ): Promise<GoEntityCardAuditEntry[]> {
    return apiRequest<GoEntityCardAuditEntry[]>(`${this.baseUrl}/${ginfObjId}/card/audit`, {
      params: { limit: params?.limit ?? 50 },
    });
  }

  async listLegitimacyOptions(): Promise<GoLegitimacyOption[]> {
    return apiRequest<GoLegitimacyOption[]>(`${this.baseUrl}/meta/legitimacy-options`);
  }

  async listCountryProfiles(): Promise<GoCountryProfileMetaItem[]> {
    return apiRequest<GoCountryProfileMetaItem[]>(`${this.baseUrl}/meta/country-profiles`);
  }

  async prepareClassifier(
    ginfObjId: number,
    payload: GoPrepareClassifierRequest,
  ): Promise<GoPrepareClassifierResponse> {
    return apiRequest<GoPrepareClassifierResponse>(
      `${this.baseUrl}/${ginfObjId}/card/prepare-classifier`,
      { method: "POST", data: payload },
    );
  }

  async listMapGeometries(ginfObjId: number): Promise<GoEntityMapGeometryItem[]> {
    return apiRequest<GoEntityMapGeometryItem[]>(`${this.baseUrl}/${ginfObjId}/map-geometries`);
  }

  async createMapGeometry(
    ginfObjId: number,
    payload: GoMapGeometryCreatePayload,
  ): Promise<GoEntityMapGeometryItem> {
    return apiRequest<GoEntityMapGeometryItem>(`${this.baseUrl}/${ginfObjId}/map-geometries`, {
      method: "POST",
      data: payload,
    });
  }

  async deleteMapGeometry(ginfObjId: number, ginfObjMapGeomId: number): Promise<void> {
    await apiRequest<void>(`${this.baseUrl}/${ginfObjId}/map-geometries/${ginfObjMapGeomId}`, {
      method: "DELETE",
    });
  }

  async listRegionLinks(ginfObjId: number): Promise<GoRegionLinkListResponse> {
    return apiRequest<GoRegionLinkListResponse>(`${this.baseUrl}/${ginfObjId}/region-links`);
  }

  async createRegionLink(ginfObjId: number, payload: GoRegionLinkCreateRequest): Promise<GoRegionLinkItem> {
    return apiRequest<GoRegionLinkItem>(`${this.baseUrl}/${ginfObjId}/region-links`, {
      method: "POST",
      data: payload,
    });
  }

  async refreshRegionLinks(ginfObjId: number): Promise<GoRegionLinkRefreshResponse> {
    return apiRequest<GoRegionLinkRefreshResponse>(`${this.baseUrl}/${ginfObjId}/region-links/refresh`, {
      method: "POST",
    });
  }

  async deleteRegionLink(ginfObjId: number, linkId: number): Promise<void> {
    await apiRequest<void>(`${this.baseUrl}/${ginfObjId}/region-links/${linkId}`, {
      method: "DELETE",
    });
  }

  async compare(leftId: number, rightId: number): Promise<GoEntityCompareResponse> {
    return apiRequest<GoEntityCompareResponse>(`${this.baseUrl}/compare`, {
      params: { left_id: leftId, right_id: rightId },
    });
  }

  async startCountryImport(payload: {
    conflict_policy?: "error" | "skip" | "update";
    dry_run?: boolean;
    package_root?: string;
    iso_filter?: string[];
  }): Promise<GoImportJob> {
    return apiRequest<GoImportJob>(`${this.baseUrl}/import/countries`, {
      method: "POST",
      data: {
        conflict_policy: payload.conflict_policy ?? "skip",
        dry_run: payload.dry_run ?? true,
        ...(payload.package_root ? { package_root: payload.package_root } : {}),
        ...(payload.iso_filter?.length ? { iso_filter: payload.iso_filter } : {}),
      },
    });
  }

  async getImportJob(jobId: number): Promise<GoImportJob> {
    return apiRequest<GoImportJob>(`${this.baseUrl}/import/jobs/${jobId}`);
  }

  async getImportCoverage(): Promise<GoSeedCoverage> {
    return apiRequest<GoSeedCoverage>(`${this.baseUrl}/import/coverage`);
  }

  async getGeometryById(geometryId: number): Promise<Geometry> {
    return apiRequest<Geometry>(`/geometries/${geometryId}`);
  }

  /** Контур привязки под правом модуля ГО (без ACL объекта ОИ). */
  async getMapGeometryContour(
    ginfObjId: number,
    ginfObjMapGeomId: number,
  ): Promise<Geometry> {
    return apiRequest<Geometry>(
      `${this.baseUrl}/${ginfObjId}/map-geometries/${ginfObjMapGeomId}/geojson`,
    );
  }

  /** Batch контуров сущности для overlay карты. */
  async listMapGeometryContours(ginfObjId: number): Promise<Geometry[]> {
    return apiRequest<Geometry[]>(`${this.baseUrl}/${ginfObjId}/map-geometries/contours`);
  }

  async listMaterialKinds(): Promise<GoMaterialKindMetaItem[]> {
    return apiRequest<GoMaterialKindMetaItem[]>(`${this.baseUrl}/meta/material-kinds`);
  }

  async listMaterials(
    ginfObjId: number,
    opts?: { kind?: string | null },
  ): Promise<GoMaterialItem[]> {
    const params =
      opts?.kind != null && opts.kind !== ""
        ? `?kind=${encodeURIComponent(opts.kind)}`
        : "";
    return apiRequest<GoMaterialItem[]>(`${this.baseUrl}/${ginfObjId}/materials${params}`);
  }

  async getPrimaryFlag(ginfObjId: number): Promise<GoMaterialItem | null> {
    return apiRequest<GoMaterialItem | null>(
      `${this.baseUrl}/${ginfObjId}/materials/primary-flag`,
    );
  }

  async createMaterial(ginfObjId: number, payload: GoMaterialCreatePayload): Promise<GoMaterialItem> {
    return apiRequest<GoMaterialItem>(`${this.baseUrl}/${ginfObjId}/materials`, {
      method: "POST",
      data: payload,
    });
  }

  async uploadMaterial(
    ginfObjId: number,
    file: File,
    opts?: { materialKind?: string | null },
  ): Promise<GoMaterialItem> {
    const formData = new FormData();
    formData.append("file", file);
    const q =
      opts?.materialKind != null && opts.materialKind !== ""
        ? `?material_kind=${encodeURIComponent(opts.materialKind)}`
        : "";
    return postFormData<GoMaterialItem>(
      `${this.baseUrl}/${ginfObjId}/materials/upload${q}`,
      formData,
    );
  }

  async downloadMaterialBlob(ginfObjId: number, ginfFileId: number): Promise<Blob> {
    return downloadFile(`${this.baseUrl}/${ginfObjId}/materials/${ginfFileId}/download`, {
      stream: true,
    });
  }

  async deleteMaterial(ginfObjId: number, ginfFileId: number): Promise<void> {
    await apiRequest<void>(`${this.baseUrl}/${ginfObjId}/materials/${ginfFileId}`, {
      method: "DELETE",
    });
  }
}

export const goEntitiesApi = new GoEntitiesService();

